import { useContext, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { PlusTierListCTABanner } from "./PlusTierListCTABanner";
import { PlusTierListCTAPage, type PlusLinkComponent } from "./PlusTierListCTAPage";
import {
  ActionTypes,
  UpdateDispatchContext,
  useUpdateState,
} from "@ugg/shared/components/SummonerProfiles/SummonerProfileUpdateReducer";
import { useSummonerProfileContext } from "@ugg/shared/components/SummonerProfiles/SummonerProfileContext";
import { useFilterManager } from "@ugg/shared/components/FilterManager/FilterManager";
import { PlusTierListFilterManager } from "./PlusTierListFilterManager";
import { useSummonerProfileInitSimple } from "@ugg/shared/api/requests/summoner-profiles/profile-init";
import { useRsoConnection } from "@ugg/shared/api/requests/accounts/oauth-hooks";
import { useLoginState } from "@ugg/shared/hooks/use-user-hooks";
import { RoleC, RoleS, normalizeRole } from "@ugg/shared/utils/role-helpers";
import { validParamsProfile } from "@ugg/shared/query-params/valid-params-profile";
import {
  QUEUE_TYPE_ID_MAP,
  CHAMPION_STATS_SEASON_OPTIONS,
  PLUS_TIER_LIST_QUEUE_TYPE_ID_OPTIONS,
} from "@ugg/shared/query-params/filter-options-profile/champion-stats";
import { INTERNAL_CURRENT_SEASON } from "@ugg/shared/utils/season-helpers";
import { useUGGPatches } from "@ugg/shared/api/requests/patches";
import { convertPatchDisplay } from "@ugg/shared/utils/patch-helpers";
import { SUMMONER_PROFILE_PAGES } from "@ugg/shared/pages/summoner-profile-pages";
import { AppRoutes, getProfilePlusTierListUrl } from "@ugg/shared/routes/app-routes";
import { PlusTierListVisualization } from "./PlusTierListVisualization";
import { useGetPersonalTierListQuery } from "@ugitgud/legos/queries/lol";
import { useRecurlySubscriptionStatus } from "@ugitgud/legos/hooks/usePremiumHooks";

export function PlusTierList({
  PlusLink,
  buildLoginHref,
  inNewWindow,
}: {
  inNewWindow?: boolean;
  PlusLink: PlusLinkComponent;
  buildLoginHref: (returnPath: string) => string;
}) {
  const dispatch = useContext(UpdateDispatchContext);
  const location = useLocation();
  const { riotUserName, riotTagLine, region, realRiotUserName } = useSummonerProfileContext();
  const { isUpdatingProfile } = useUpdateState();
  const { data: profileData } = useSummonerProfileInitSimple(region, riotUserName, riotTagLine, {});
  const profileIsPremium = profileData?.profileInitSimple?.premium;
  const { isActive: viewerIsPremium } = useRecurlySubscriptionStatus();
  const { summoner: viewerRsoSummoner } = useRsoConnection();
  const isLoggedIn = useLoginState();
  const { data: patches } = useUGGPatches();

  const { filters } = useFilterManager(SUMMONER_PROFILE_PAGES.PLUS_TIER_LIST, validParamsProfile);

  const queueTypes: number[] = QUEUE_TYPE_ID_MAP[filters.queueType as keyof typeof QUEUE_TYPE_ID_MAP] ?? [];
  const hasRole = PLUS_TIER_LIST_QUEUE_TYPE_ID_OPTIONS.some(
    ({ value, reset }) => value === filters.queueType && !reset?.includes("role"),
  );
  const isLatestSeason = parseInt(filters.season) === INTERNAL_CURRENT_SEASON;
  const seasonContext = isLatestSeason
    ? patches?.[0]
      ? `patch ${convertPatchDisplay(patches[0]).replace("_", ".")}`
      : ""
    : CHAMPION_STATS_SEASON_OPTIONS.find((o) => o.value === filters.season)?.label ?? "";

  const fetchPolicy = isUpdatingProfile ? ("network-only" as const) : ("cache-and-network" as const);

  const { data, loading, error, refetch } = useGetPersonalTierListQuery({
    ssr: false,
    fetchPolicy,
    skip: queueTypes.length === 0,
    variables: {
      riotUserName,
      riotTagLine,
      regionId: region,
      queueIds: queueTypes,
      role: RoleC.convertToInt(hasRole ? normalizeRole(filters.role, RoleS.ALL) : RoleS.ALL),
    },
  });

  useEffect(() => {
    dispatch({
      type: ActionTypes.ADD_UPDATES,
      payload: { playerChampionStats: refetch },
    });
  }, [filters.queueType, filters.role]);

  if (!profileIsPremium) {
    let ClickHereButton: PlusLinkComponent;
    let clickHereLink: string;
    if (!isLoggedIn) {
      ClickHereButton = Link;
      clickHereLink = buildLoginHref(`${location.pathname}${location.search}`);
    } else if (!viewerRsoSummoner) {
      ClickHereButton = PlusLink;
      clickHereLink = AppRoutes.SETTINGS_ACCOUNT;
    } else if (viewerIsPremium) {
      ClickHereButton = Link;
      clickHereLink = getProfilePlusTierListUrl(
        viewerRsoSummoner.regionId,
        viewerRsoSummoner.riotUserName,
        viewerRsoSummoner.riotTagLine,
      );
    } else {
      ClickHereButton = PlusLink;
      clickHereLink = AppRoutes.PLUS;
    }
    return (
      <PlusTierListCTAPage className="mt-9" PlusLink={PlusLink} ClickHereButton={ClickHereButton} clickHereLink={clickHereLink} />
    );
  }

  return (
    <div className="w-full">
      {!viewerIsPremium && <PlusTierListCTABanner className="mb-6" inNewWindow={inNewWindow} />}
      <PlusTierListFilterManager hasRole={hasRole} className="mb-[24px]" />
      {loading ? (
        <div className="rounded-[3px] bg-purple-400 w-full min-h-[300px] flex items-center justify-center">
          <div className="spinthatshit-loader">
            <div className="spinner" />
          </div>
        </div>
      ) : error ? (
        <div className="rounded-[3px] bg-purple-400 w-full min-h-[300px] flex items-center justify-center">
          <span className="text-lavender-50 text-[14px]">Something went wrong. Please try again later.</span>
        </div>
      ) : (
        <PlusTierListVisualization
          stats={data?.getPersonalTierList || []}
          username={realRiotUserName || riotUserName}
          filters={filters}
          seasonContext={seasonContext}
        />
      )}
    </div>
  );
}
